// src/components/admin-agent-questions/QuestionLibraryManager.jsx
import { useMemo, useState } from "react";
import Button from "@components/ui/Button";
import Input from "@components/ui/Input";

function EnumHint() {
  return (
    <div className="text-xs text-gray-500">
      Use UPPER_SNAKE_CASE. Example:{" "}
      <span className="font-mono">NAME_ANY_CONFIRMATION</span>
    </div>
  );
}

function RulesEditor({ rules, onChange, onAnyChange }) {
  const [draft, setDraft] = useState("");

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-600">Rules</div>

      {(rules || []).length === 0 ? (
        <div className="text-sm text-gray-500">No rules yet.</div>
      ) : (
        <ul className="space-y-2">
          {rules.map((r, idx) => (
            <li key={idx} className="flex gap-2 items-start">
              <div className="flex-1 text-sm text-gray-800">{r}</div>
              <Button
                type="button"
                size="xs"
                color="danger"
                onClick={() => {
                  onAnyChange?.();
                  onChange(rules.filter((_, i) => i !== idx));
                }}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <Input
          placeholder="Add a rule…"
          value={draft}
          onChange={(e) => {
            onAnyChange?.();
            setDraft(e.target.value);
          }}
        />
        <Button
          type="button"
          onClick={() => {
            const v = draft.trim();
            if (!v) return;
            onAnyChange?.();
            onChange([...(rules || []), v]);
            setDraft("");
          }}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function ErrorBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div className="border border-red-200 bg-red-50 text-red-700 rounded-lg px-3 py-2 text-sm flex items-start justify-between gap-3">
      <div>{message}</div>
      <button
        type="button"
        className="text-red-700/70 hover:text-red-900 text-xs"
        onClick={onDismiss}
      >
        Dismiss
      </button>
    </div>
  );
}

export default function QuestionLibraryManager({
  query,
  onQueryChange,
  questions,
  onCreate,
  onUpdate,
  onDelete,
  error,
  onClearError,
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    agent_question_enum: "",
    question: "",
    rules: [],
  });

  const resetForm = () =>
    setForm({ agent_question_enum: "", question: "", rules: [] });

  const canCreate = useMemo(() => {
    return form.agent_question_enum.trim() && form.question.trim();
  }, [form]);

  const createError = error?.scope === "create" ? error.message : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-lg font-semibold">Question Library</div>
          <div className="text-xs text-gray-500">
            Create questions and rules that can be used in defaults and clinic
            flows.
          </div>
        </div>

        <div className="flex gap-2 w-full sm:w-[520px]">
          <div className="flex-1">
            <Input
              placeholder="Search questions…"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
            />
          </div>
          <Button
            type="button"
            onClick={() => {
              onClearError?.();
              setCreateOpen((v) => !v);
              resetForm();
            }}
          >
            {createOpen ? "Close" : "New Question"}
          </Button>
        </div>
      </div>

      {createOpen ? (
        <div className="border rounded-lg bg-white p-4 space-y-4">
          <div className="text-sm font-semibold">Create Question</div>

          <ErrorBanner message={createError} onDismiss={onClearError} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input
                label="Question Enum"
                value={form.agent_question_enum}
                onChange={(e) => {
                  onClearError?.();
                  setForm((p) => ({
                    ...p,
                    agent_question_enum: e.target.value,
                  }));
                }}
                placeholder="NAME_ANY_CONFIRMATION"
              />
              <EnumHint />
            </div>

            <Input
              label="Question Label"
              value={form.question}
              onChange={(e) => {
                onClearError?.();
                setForm((p) => ({ ...p, question: e.target.value }));
              }}
              placeholder="Patient Name"
            />
          </div>

          <RulesEditor
            rules={form.rules}
            onAnyChange={onClearError}
            onChange={(next) => setForm((p) => ({ ...p, rules: next }))}
          />

          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              color="light"
              onClick={() => {
                onClearError?.();
                setCreateOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!canCreate}
              onClick={async () => {
                const ok = await onCreate(form);
                if (ok) {
                  setCreateOpen(false);
                  resetForm();
                }
              }}
            >
              Create
            </Button>
          </div>
        </div>
      ) : null}

      <div className="border rounded-lg bg-white overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold">
          Questions ({questions.length})
        </div>

        {questions.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">No questions found.</div>
        ) : (
          <div className="divide-y">
            {questions.map((q) => (
              <QuestionRow
                key={q.agent_question_enum}
                q={q}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionRow({ q, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState({
    question: q.question,
    rules: q.rules || [],
  });

  const dirty =
    local.question !== q.question ||
    JSON.stringify(local.rules || []) !== JSON.stringify(q.rules || []);

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900">
            {q.question}
          </div>
          <div className="text-xs text-gray-500 font-mono break-all">
            {q.agent_question_enum}
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            color="light"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Hide" : "Edit"}
          </Button>
          <Button
            type="button"
            size="sm"
            color="danger"
            onClick={() => onDelete(q.agent_question_enum)}
          >
            Delete
          </Button>
        </div>
      </div>

      {open ? (
        <div className="mt-4 border rounded-lg bg-gray-50 p-4 space-y-4">
          <Input
            label="Question Label"
            value={local.question}
            onChange={(e) =>
              setLocal((p) => ({ ...p, question: e.target.value }))
            }
          />

          <RulesEditor
            rules={local.rules}
            onChange={(next) => setLocal((p) => ({ ...p, rules: next }))}
          />

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              color="light"
              onClick={() =>
                setLocal({ question: q.question, rules: q.rules || [] })
              }
            >
              Reset
            </Button>
            <Button
              type="button"
              disabled={!dirty}
              onClick={() => onUpdate(q.agent_question_enum, local)}
            >
              Save
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
